import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Users, Clock, Shield, Plus, Trash2, CheckCircle, XCircle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ROLE_COLORS = {
  admin:  'bg-blue-50 text-blue-700 border border-blue-200',
  editor: 'bg-amber-50 text-amber-700 border border-amber-200',
  caster: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const ROLES = ['caster', 'editor', 'admin'];

function Avatar({ name, picture, size = 8 }) {
  if (picture) {
    return <img src={picture} alt={name} className={`w-${size} h-${size} rounded-full object-cover shrink-0`} />;
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center shrink-0`}>
      <span className="text-blue-600 text-sm font-medium">{(name || '?')[0].toUpperCase()}</span>
    </div>
  );
}

function RoleDropdown({ currentRole, userId, onRoleChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = async (role) => {
    if (role === currentRole) { setOpen(false); return; }
    setLoading(true);
    try {
      await api.put(`/users/${userId}/role`, { role });
      onRoleChange(userId, role);
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded capitalize ${ROLE_COLORS[currentRole]} cursor-pointer hover:opacity-80 transition-opacity`}
      >
        {currentRole}
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[100px]">
            {ROLES.map(r => (
              <button
                key={r}
                onClick={() => handleChange(r)}
                className={`w-full text-left px-3 py-1.5 text-xs capitalize hover:bg-slate-50 transition-colors ${r === currentRole ? 'text-slate-900 font-medium' : 'text-slate-600'}`}
              >
                {r}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Active Users ───
function ActiveUsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = (userId, newRole) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const handleRevoke = async (userId, name) => {
    if (!window.confirm(`Revoke access for ${name}? They will need re-approval to log in.`)) return;
    try {
      await api.put(`/users/${userId}/revoke`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Access revoked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke access');
    }
  };

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading...</div>;

  return (
    <div className="space-y-2">
      {users.length === 0 && (
        <div className="text-slate-400 text-sm text-center py-8">No active users found.</div>
      )}
      {users.map(u => (
        <div key={u.id} className="flex items-center gap-4 bg-white rounded-xl px-4 py-3 border border-slate-200 shadow-sm">
          <Avatar name={u.name} picture={u.picture} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{u.name || '—'}</p>
            <p className="text-xs text-slate-500 truncate">{u.email}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-slate-400 hidden sm:block">
              {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
            </span>
            <RoleDropdown currentRole={u.role} userId={u.id} onRoleChange={handleRoleChange} />
            {u.id !== currentUserId && (
              <button
                onClick={() => handleRevoke(u.id, u.name || u.email)}
                className="text-slate-400 hover:text-red-500 transition-colors p-1"
                title="Revoke access"
              >
                <XCircle size={16} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tab: Pending Requests ───
function PendingTab({ onCountChange }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState({});
  const [roleSelections, setRoleSelections] = useState({});

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get('/users/pending');
      setPending(res.data);
      onCountChange(res.data.length);
    } catch {
      toast.error('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (userId, name) => {
    const role = roleSelections[userId] || 'caster';
    setApproving(prev => ({ ...prev, [userId]: 'approving' }));
    try {
      await api.put(`/users/${userId}/approve`, { role });
      setPending(prev => prev.filter(u => u.id !== userId));
      onCountChange(pending.length - 1);
      toast.success(`${name || 'User'} approved as ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve user');
    } finally {
      setApproving(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleReject = async (userId, name) => {
    setApproving(prev => ({ ...prev, [userId]: 'rejecting' }));
    try {
      await api.put(`/users/${userId}/reject`);
      setPending(prev => prev.filter(u => u.id !== userId));
      onCountChange(pending.length - 1);
      toast.success(`${name || 'User'} rejected`);
    } catch {
      toast.error('Failed to reject user');
    } finally {
      setApproving(prev => ({ ...prev, [userId]: null }));
    }
  };

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading...</div>;

  if (pending.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle size={40} className="text-emerald-500 mx-auto mb-3" />
        <p className="text-slate-900 font-medium">All clear</p>
        <p className="text-slate-500 text-sm mt-1">No pending access requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {pending.map(u => (
        <div key={u.id} className="bg-white rounded-xl px-4 py-4 border border-amber-200 shadow-sm">
          <div className="flex items-start gap-4">
            <Avatar name={u.name} picture={u.picture} size={10} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900">{u.name || '—'}</p>
              <p className="text-xs text-slate-500">{u.email}</p>
              <p className="text-xs text-slate-400 mt-1">
                Requested {u.requested_at ? new Date(u.requested_at).toLocaleString() : 'recently'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-500 shrink-0">Assign role:</span>
            <select
              value={roleSelections[u.id] || 'caster'}
              onChange={e => setRoleSelections(prev => ({ ...prev, [u.id]: e.target.value }))}
              className="bg-white border border-slate-200 text-slate-700 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500"
            >
              {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
            </select>
            <div className="flex gap-2 ml-auto">
              <Button
                size="sm"
                onClick={() => handleReject(u.id, u.name)}
                disabled={!!approving[u.id]}
                className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs h-8"
              >
                <XCircle size={14} className="mr-1" />
                {approving[u.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
              </Button>
              <Button
                size="sm"
                onClick={() => handleApprove(u.id, u.name)}
                disabled={!!approving[u.id]}
                className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs h-8"
              >
                <CheckCircle size={14} className="mr-1" />
                {approving[u.id] === 'approving' ? 'Approving...' : 'Approve'}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tab: Allowlist ───
function AllowlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await api.get('/users/approved-emails');
      setEntries(res.data);
    } catch {
      toast.error('Failed to load allowlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setAdding(true);
    try {
      const res = await api.post('/users/approved-emails', { email });
      setEntries(prev => [res.data, ...prev]);
      setNewEmail('');
      toast.success(`${email} added to allowlist`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add email');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from allowlist? They will lose access.`)) return;
    try {
      await api.delete(`/users/approved-emails/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Email removed from allowlist');
    } catch {
      toast.error('Failed to remove email');
    }
  };

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
        <p className="text-sm text-slate-700 mb-3 font-medium">Pre-approve an email</p>
        <p className="text-xs text-slate-500 mb-3">Users on this list will get instant access when they sign in with Google — no approval required.</p>
        <div className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="name@example.com"
            className="flex-1 bg-white border border-slate-200 text-slate-900 text-sm rounded-lg px-3 py-2 placeholder-slate-400 focus:outline-none focus:border-blue-500"
          />
          <Button
            onClick={handleAdd}
            disabled={adding || !newEmail.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 shrink-0"
          >
            <Plus size={16} className="mr-1" />
            {adding ? 'Adding...' : 'Add'}
          </Button>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">No pre-approved emails yet.</div>
      ) : (
        <div className="space-y-2">
          {entries.map(e => (
            <div key={e.id} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 shadow-sm">
              <Shield size={16} className="text-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 truncate">{e.email}</p>
                <p className="text-xs text-slate-400">
                  Added {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                  {e.added_by_name ? ` by ${e.added_by_name}` : ''}
                </p>
              </div>
              <button
                onClick={() => handleRemove(e.id, e.email)}
                className="text-slate-400 hover:text-red-500 transition-colors p-1 shrink-0"
                title="Remove from allowlist"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───
export default function UsersPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [pendingCount, setPendingCount] = useState(0);

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const tabs = [
    { id: 'active',    label: 'Active Users',     icon: Users },
    { id: 'pending',   label: 'Pending Requests',  icon: Clock,   badge: pendingCount },
    { id: 'allowlist', label: 'Allowlist',          icon: Shield },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
        <p className="text-slate-500 text-sm mt-1">Manage who can access Okebourne</p>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 mb-6 w-fit">
        {tabs.map(({ id, label, icon: Icon, badge }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === id
                ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Icon size={15} />
            {label}
            {badge > 0 && (
              <span className="bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'active'    && <ActiveUsersTab currentUserId={user?.user_id} />}
      {activeTab === 'pending'   && <PendingTab onCountChange={setPendingCount} />}
      {activeTab === 'allowlist' && <AllowlistTab />}
    </div>
  );
}